import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import {
  MercerAssignmentSearchablePropertyEnum,
  MercerJobCodeSearchablePropertyEnum
} from '@coin/shared/master-data/util';
import { HttpHelpersService, LoadingService } from '@coin/shared/core/data-access';
import { environment } from '@coin/shared/core/util-environments';
import {
  EmployeeMercerData,
  EmployeeMercerDataSlim,
  FieldValuesDescription,
  MercerData,
  PaginatedResult,
  SimpleTableService,
  TransactionStatus,
  TransactionStatusMetadata
} from '@coin/shared/core/util-models';
import { ToastrService } from 'ngx-toastr';
import { Observable, of, throwError } from 'rxjs';
import { catchError, finalize, map, switchMap, tap } from 'rxjs/operators';

export interface MercerDataUploadResponse {
  inserted: number;
  updated: number;
}

const rankOrderCareerStream = {
  Executive: 1,
  Management: 2,
  Professional: 3,
  'Para-Professional': 4
};

@Injectable({
  providedIn: 'root'
})
export class MercerDataService implements SimpleTableService {
  private readonly httpClient = inject(HttpClient);
  private readonly loading = inject(LoadingService);
  private readonly toast = inject(ToastrService);
  private readonly httpHelpersService = inject(HttpHelpersService);

  queryPagedAndFilteredData = this.getAssignments;

  public getAssignments(
    page: number,
    size: number,
    searchText?: string,
    filterText?: string,
    sortText?: string
  ): Observable<PaginatedResult<EmployeeMercerData>> {
    return this.httpClient
      .get<
        PaginatedResult<EmployeeMercerData>
      >(`${environment.api.baseUrl}/admin/api/v1/master/mercer-job-codes/assignments?Page=${page + 1}&Size=${size}${filterText}${sortText}`)
      .pipe(
        map(result => ({
          ...result,
          content: result?.content?.map(item => new EmployeeMercerData(item))
        })),
        catchError(() => of(null)) // do not cancel subscription in simple table component
      );
  }

  public getAssignmentFieldValues(
    page: number,
    size: number,
    targetProperty: MercerAssignmentSearchablePropertyEnum | string,
    searchText?: string
  ): Observable<PaginatedResult<string>> {
    const searchQuery = searchText.trim() ? `&${targetProperty}=${searchText}` : '';

    return this.httpClient
      .get<
        PaginatedResult<string>
      >(`${environment.api.baseUrl}/admin/api/v1/master/mercer-job-codes/assignments/field-values?Page=${page + 1}&Size=${size}&targetProperty=${targetProperty}${searchQuery}`)
      .pipe(
        catchError(() => of(null)) // do not cancel subscription in simple table component
      );
  }

  public getPositionClassByOrgIdAndJobCode(orgId: string, jobCode: string): Observable<number> {
    return this.httpClient
      .get<number>(
        `${environment.api.baseUrl}/admin/api/v3/master/position-classes?orgId=${encodeURIComponent(orgId)}&jobCode=${encodeURIComponent(jobCode)}`
      )
      .pipe(this.loading.withLoadingScreen, this.httpHelpersService.handleError('Cannot get position class'));
  }

  public getPositionClassByOrgCodeAndJobCode(orgCode: string, jobCode: string): Observable<number> {
    return this.httpClient
      .get<number>(
        `${environment.api.baseUrl}/admin/api/v3/master/position-classes?orgCode=${encodeURIComponent(orgCode)}&jobCode=${encodeURIComponent(jobCode)}`
      )
      .pipe(
        this.loading.withLoadingScreen,
        catchError((error: HttpErrorResponse) => {
          const backendMessage = error?.error?.message ?? error.message;
          this.toast.error(backendMessage);
          return throwError(() => error);
        })
      );
  }

  public getMercerFieldValues(
    page: number,
    size: number,
    key: MercerJobCodeSearchablePropertyEnum | string,
    mercerData: MercerData,
    search?: string,
    fullFilter?: boolean
  ): Observable<PaginatedResult<string>> {
    let filterText = search?.trim() ? `&${key}=${encodeURIComponent(search)}` : '';
    filterText += mercerData ? this.getAdditionalFilterTextFieldValues(mercerData, key, fullFilter) : '';

    return this.httpClient
      .get<
        PaginatedResult<string>
      >(`${environment.api.baseUrl}/admin/api/v1/master/mercer-job-codes/field-values?Page=${page + 1}&Size=${size}${filterText}&targetProperty=${key}`)
      .pipe(catchError(() => of(null)));
  }

  public getNonPartialMercerFieldValues(
    ...args: Parameters<MercerDataService['getMercerFieldValues']>
  ): Observable<PaginatedResult<string>> {
    return this.getMercerFieldValues(...args).pipe(
      map(result => ({
        ...result,
        content: result.content.filter(code => !code.includes('XXX'))
      }))
    );
  }

  public getMercerJobProfileFieldValues(
    size: number,
    keys: Array<MercerJobCodeSearchablePropertyEnum | string>,
    mercerData: MercerData
  ): Observable<Record<string, string[]>> {
    // find longest filter text in keys
    const filterText = keys.reduce((previousFilter, key) => {
      const filter = mercerData ? this.getAdditionalFilterTextFieldValues(mercerData, key) : '';
      return previousFilter.length > filter.length ? previousFilter : filter;
    }, '');
    const textAsParams = new HttpParams({ fromString: filterText }).append('Size', size).delete('');

    const params = textAsParams
      .keys()
      .reduce((par, key) => par.append(`Query.${key}`, textAsParams.get(key)), new HttpParams());
    return this.httpClient
      .get<
        Record<string, string[]>
      >(`${environment.api.baseUrl}/admin/api/v1/master/mercer-job-codes/job-profile-values`, { params })
      .pipe(catchError(() => of(null)));
  }

  public getMercerFieldValuesWithDescription(
    page: number,
    size: number,
    key: MercerJobCodeSearchablePropertyEnum | string,
    mercerData: MercerData,
    search?: string,
    sortQuery?: string
  ): Observable<PaginatedResult<FieldValuesDescription>> {
    this.loading.present();
    let filterText = search?.trim() ? `&${key}=${encodeURIComponent(search)}` : '';
    filterText += this.getAdditionalFilterTextMercerData(mercerData, key, true);

    return this.httpClient
      .get<
        PaginatedResult<FieldValuesDescription>
      >(`${environment.api.baseUrl}/admin/api/v1/master/mercer-job-codes/field-values-with-description?Pageable.Page=${page + 1}&Pageable.Size=${size}${filterText}&targetProperty=${key}${sortQuery ?? ''}`)
      .pipe(
        map(result => ({
          ...result,
          content:
            key === MercerJobCodeSearchablePropertyEnum.CareerStream
              ? result?.content?.map(value => {
                  value.rank = rankOrderCareerStream[value.title];
                  return value;
                })
              : result?.content
        })),
        catchError(() => of(null)),
        finalize(() => this.loading.dismiss())
      );
  }

  public getCorrelatingMercerData(
    mercerData: MercerData,
    key: MercerJobCodeSearchablePropertyEnum,
    isEqual?: boolean,
    partialJobCode = false
  ): Observable<MercerData> {
    let filterText = `&${key + (isEqual ? 'Eq' : '')}=${encodeURIComponent(mercerData?.[key])}`;
    filterText += this.getAdditionalFilterTextMercerData(mercerData, key, isEqual);

    const params = new HttpParams().append('partialJobCode', partialJobCode);

    return this.httpClient
      .get<
        PaginatedResult<MercerData>
      >(`${environment.api.baseUrl}/admin/api/v1/master/mercer-job-codes?Page=1&Size=1${filterText}`, { params })
      .pipe(
        map(result => new MercerData(result.content?.[0])),
        catchError(() => of(null))
      );
  }

  private getAdditionalFilterTextFieldValues(mercerData: MercerData, key: string, fullFilter?: boolean): string {
    let filterText = '';
    let additionalFilters: string[] = [];

    if (fullFilter) {
      additionalFilters = [
        'familyCode',
        'family',
        'subFamilyCode',
        'subFamily',
        'specializationCode',
        'specialization',
        'careerStream',
        'careerLevel',
        'careerLevelShort'
      ];
    } else if (key.includes('subFamily')) {
      additionalFilters = ['familyCode', 'family', 'careerStream'];
    } else if (key.includes('specialization')) {
      additionalFilters = ['familyCode', 'family', 'subFamilyCode', 'subFamily', 'careerStream'];
    } else if (key.includes('careerStream')) {
      additionalFilters = [
        'familyCode',
        'family',
        'subFamilyCode',
        'subFamily',
        'specializationCode',
        'specialization'
      ];
    } else if (key.includes('careerLevel')) {
      additionalFilters = [
        'familyCode',
        'family',
        'subFamilyCode',
        'subFamily',
        'specializationCode',
        'specialization',
        'careerStream'
      ];
    }
    if (additionalFilters?.length) {
      for (const filterKey of additionalFilters) {
        filterText += mercerData[filterKey] ? `&${`${filterKey}Eq`}=${encodeURIComponent(mercerData[filterKey])}` : '';
      }
    }

    return filterText;
  }

  private getAdditionalFilterTextMercerData(mercerData: MercerData, key: string, isEqual?: boolean): string {
    let filterText = '';
    let additionalFilters: string[] = [];

    // With Eq BE performs equal not contains
    if (key.includes('subFamily')) {
      additionalFilters = ['familyCode', 'family', 'careerStream'];
    } else if (key.includes('specialization')) {
      additionalFilters = ['familyCode', 'family', 'subFamilyCode', 'subFamily', 'careerStream'];
    } else if (key.includes('careerStream')) {
      additionalFilters = [
        'familyCode',
        'family',
        'subFamilyCode',
        'subFamily',
        'specializationCode',
        'specialization'
      ];
    } else if (key.includes('careerLevel')) {
      additionalFilters = [
        'familyCode',
        'family',
        'subFamilyCode',
        'subFamily',
        'specializationCode',
        'specialization',
        'careerStream'
      ];
    }

    if (additionalFilters?.length) {
      for (const filterKey of additionalFilters) {
        filterText += mercerData[filterKey]
          ? `&${filterKey + (isEqual ? 'Eq' : '')}=${encodeURIComponent(mercerData[filterKey])}`
          : '';
      }
    }

    return filterText;
  }

  public getHistoricEntries(gid: string): Observable<EmployeeMercerDataSlim[]> {
    return this.httpClient
      .get<
        EmployeeMercerDataSlim[]
      >(`${environment.api.baseUrl}/admin/api/v1/master/mercer-job-codes/employees?gid=${gid}`)
      .pipe(finalize(() => this.loading.dismiss()));
  }

  public createAssignment(body: EmployeeMercerDataSlim): Observable<EmployeeMercerDataSlim> {
    this.loading.present();
    return this.httpClient
      .post<EmployeeMercerDataSlim>(`${environment.api.baseUrl}/admin/api/v1/master/mercer-job-codes/employees`, body)
      .pipe(
        tap(() => this.toast.success(`Assignment created.`)),
        catchError((res: HttpErrorResponse) => {
          this.toast.error(`Error trying to create assignment: ${res.error?.title ? res.error?.title : res.error}`);
          return throwError(res.error);
        }),
        finalize(() => this.loading.dismiss())
      );
  }

  public updateAssignment(body: EmployeeMercerDataSlim): Observable<void> {
    this.loading.present();
    return this.httpClient
      .patch<void>(`${environment.api.baseUrl}/admin/api/v1/master/mercer-job-codes/employees`, body)
      .pipe(
        tap(() => this.toast.success(`Assignment updated.`)),
        catchError((res: HttpErrorResponse) => {
          this.toast.error(`Error trying to update assignment: ${res.error?.title ? res.error?.title : res.error}`);
          return throwError(res.error);
        }),
        finalize(() => this.loading.dismiss())
      );
  }

  public upload(file: File): Observable<TransactionStatus<TransactionStatusMetadata>> {
    this.loading.present();
    return this.httpClient
      .get<{
        signedUrl: string;
      }>(`${environment.api.documentsUrl}/mercer-jobcode/upload/${encodeURIComponent(file.name)}`)
      .pipe(
        switchMap(({ signedUrl }) =>
          this.httpClient.put<TransactionStatus<TransactionStatusMetadata>>(signedUrl, file)
        ),
        tap(() => this.toast.success('Uploaded mercer assignments')),
        catchError((res: HttpErrorResponse) => {
          this.toast.error(`Error trying to upload data: ${res}`);
          return throwError(res);
        }),
        finalize(() => this.loading.dismiss())
      );
  }

  public getMercerDataByJobCodeId(id: string): Observable<MercerData> {
    this.loading.present();
    return this.httpClient
      .get<MercerData>(`${environment.api.baseUrl}/admin/api/v1/master/mercer-job-codes/${id}`)
      .pipe(
        this.httpHelpersService.handleError('Cannot get job code'),
        finalize(() => this.loading.dismiss())
      );
  }

  public getMercerDataByJobCode(jobCode: string): Observable<MercerData> {
    return this.httpClient
      .get<
        PaginatedResult<MercerData>
      >(`${environment.api.baseUrl}/admin/api/v1/master/mercer-job-codes?Page=1&Size=1&jobCode=${encodeURIComponent(jobCode)}`)
      .pipe(
        map(result => new MercerData(result.content?.[0])),
        catchError(() => of(null))
      );
  }
}
